import React from 'react';
import { Form, Badge, Radio } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved, faPen, faMobileScreen, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { CheckOutlined } from '@ant-design/icons';
import {
  SettingsCard,
  CardHeader,
  CardTitle,
  CardTitleText,
  PreferenceRow,
  PreferenceInfo,
  PreferenceLabel,
  PreferenceDescription,
  FormSection,
  SaveChangesButton,
  CancelButton,
  ActionButtons,
  StyledSwitch,
  ActionIconButton,
  StyledInput,
  TwoStepMethodOption,
  TwoStepInputWrap,
  TwoStepEnableBlock,
  TwoStepEnableTitle,
  TwoStepEnableDescription,
  TwoStepSwitchWrap,
} from './Styles';
import type { TwoStepVerificationSectionProps, TwoFactorMethod } from './types';

const maskValue = (value: string): string => {
  if (!value || value.length < 4) return value || '—';
  return value.slice(0, 2) + '****' + value.slice(-2);
};

const TwoStepVerificationSection: React.FC<TwoStepVerificationSectionProps> = ({
  editingSection,
  formData,
  loading,
  profileLoading,
  onEdit,
  onSwitchChange,
  onInputChange,
  onMethodChange,
  onSave,
  onCancel,
}) => {
  const isEditing = editingSection === 'twoStep';
  const displayMethod = formData.twoFactorMethod === 'phone' ? 'Phone' : 'Email';
  const displayValue =
    formData.twoFactorMethod === 'phone'
      ? maskValue(formData.twoFactorPhone)
      : maskValue(formData.twoFactorEmail);

  return (
    <SettingsCard>
      <CardHeader>
        <CardTitle>
          <FontAwesomeIcon icon={faShieldHalved} style={{ fontSize: 20, color: '#1890ff' }} />
          <CardTitleText level={4}>Two-Step Verification</CardTitleText>
        </CardTitle>
        <ActionIconButton
          onClick={profileLoading ? undefined : onEdit}
          iconColor="#2D6CCA"
          style={{ opacity: profileLoading ? 0.5 : 1, cursor: profileLoading ? 'not-allowed' : 'pointer' }}
        >
          <FontAwesomeIcon icon={faPen} fontSize={14} />
        </ActionIconButton>
      </CardHeader>

      {!isEditing ? (
        <>
          <PreferenceRow>
            <FontAwesomeIcon icon={faShieldHalved} style={{ fontSize: 16, color: '#1890ff' }} />
            <PreferenceInfo>
              <PreferenceLabel>Two-Step Verification</PreferenceLabel>
              <PreferenceDescription>
                Add an extra layer of security using your personal phone number or email
              </PreferenceDescription>
            </PreferenceInfo>
            <Badge
              status={formData.twoFactorEnabled ? 'success' : 'default'}
              text={formData.twoFactorEnabled ? 'Enabled' : 'Disabled'}
            />
          </PreferenceRow>
          {formData.twoFactorEnabled && (
            <PreferenceRow>
              <PreferenceInfo>
                <PreferenceLabel>Verification method</PreferenceLabel>
                <PreferenceDescription>
                  {displayMethod}: {displayValue}
                </PreferenceDescription>
              </PreferenceInfo>
            </PreferenceRow>
          )}
        </>
      ) : (
        <FormSection>
          <Form layout="vertical">
            <Form.Item label="Enable Two-Step Verification">
              <TwoStepEnableBlock>
                <PreferenceInfo>
                  <TwoStepEnableTitle>Two-Step Verification</TwoStepEnableTitle>
                  <TwoStepEnableDescription>
                    Verify your identity using a code sent to your personal phone or email
                  </TwoStepEnableDescription>
                </PreferenceInfo>
                <TwoStepSwitchWrap>
                  <StyledSwitch
                    checked={formData.twoFactorEnabled}
                    onChange={(checked) => onSwitchChange('twoFactorEnabled', checked)}
                  />
                </TwoStepSwitchWrap>
              </TwoStepEnableBlock>
            </Form.Item>

          {formData.twoFactorEnabled && (
            <>
              <Form.Item label="Verification method">
                <Radio.Group
                  value={formData.twoFactorMethod}
                  onChange={(e) => onMethodChange(e.target.value as TwoFactorMethod)}
                >
                  <TwoStepMethodOption value="phone">
                    <FontAwesomeIcon icon={faMobileScreen} />
                    <span>Personal phone number</span>
                  </TwoStepMethodOption>
                  <TwoStepMethodOption value="email">
                    <FontAwesomeIcon icon={faEnvelope} />
                    <span>Personal email</span>
                  </TwoStepMethodOption>
                </Radio.Group>
              </Form.Item>

              {formData.twoFactorMethod === 'phone' && (
                <Form.Item label="Personal phone number">
                  <TwoStepInputWrap>
                    <StyledInput
                      name="twoFactorPhone"
                      value={formData.twoFactorPhone}
                      onChange={onInputChange}
                      placeholder="e.g. +1 (416) 555-1234"
                      type="tel"
                      maxLength={20}
                      aria-label="Personal phone number for two-step verification"
                    />
                  </TwoStepInputWrap>
                </Form.Item>
              )}

              {formData.twoFactorMethod === 'email' && (
                <Form.Item label="Personal email">
                  <TwoStepInputWrap>
                    <StyledInput
                      name="twoFactorEmail"
                      value={formData.twoFactorEmail}
                      onChange={onInputChange}
                      placeholder="e.g. you@example.com"
                      type="email"
                      aria-label="Personal email for two-step verification"
                    />
                  </TwoStepInputWrap>
                </Form.Item>
              )}
            </>
          )}

          </Form>
          <ActionButtons>
            <SaveChangesButton icon={<CheckOutlined />} onClick={onSave} loading={loading}>
              Save Changes
            </SaveChangesButton>
            <CancelButton onClick={onCancel}>Cancel</CancelButton>
          </ActionButtons>
        </FormSection>
      )}
    </SettingsCard>
  );
};

export default React.memo(TwoStepVerificationSection);
